import { useEffect } from 'react';
import { fabric } from 'fabric';
import { colorToRgba } from '../utils/colorUtils';
import { createPolygonObject } from '../utils/loadImageToCanvas';
import useDetectionStore from '../store/useDetectionStore';

const usePolygonDrawing = (canvas, mode, polygonPoints, polygonObj, isDrawingPolygon, currentClassId, zoomLevel, getCurrentImage, updatePolygons, setPolygonPoints, setPolygonObj, setIsDrawingPolygon, classes, BASE_STROKE_WIDTH, saveState) => {
    useEffect(() => {
        if (!canvas || mode !== 'polygon') return;
        const selectedClass = classes.find(cls => cls.id === currentClassId);
        const finishPolygon = () => {
            if (polygonPoints.length >= 3) saveState?.();

            if (polygonPoints.length < 3) {
                if (polygonObj) {
                    canvas.remove(polygonObj);
                    setPolygonObj(null);
                }
                setPolygonPoints([]);
                return;
            }

            const currentImage = getCurrentImage();
            const normalizedPoints = polygonPoints.map(p => ({
                x: p.x / currentImage.originalWidth,
                y: p.y / currentImage.originalHeight
            }));

            const record = { classId: currentClassId, points: normalizedPoints };
            const newPolygons = [...currentImage.polygons, record];

            updatePolygons(newPolygons);

            // built from the stored record through the shared builder, so a
            // freshly drawn polygon is identical to a reloaded one
            const finalPolygon = createPolygonObject(record, newPolygons.length - 1, {
                width: currentImage.originalWidth,
                height: currentImage.originalHeight,
                classes,
                zoom: zoomLevel || 1,
                baseStroke: BASE_STROKE_WIDTH,
                showFill: useDetectionStore.getState().showFill,
            });
            if (polygonObj) {
                canvas.remove(polygonObj);
            }
            canvas.add(finalPolygon);

            setPolygonObj(null);
            setPolygonPoints([]);
            setIsDrawingPolygon(false);
            canvas.requestRenderAll();
        };

        const onMouseDown = (o) => {
            const currentImage = getCurrentImage();
            if (!currentImage || !isDrawingPolygon) return;

            if (!selectedClass) {
                // without this a polygon was saved against a class id that has
                // no class behind it — highlight the panel and drop the click
                useDetectionStore.getState().flagClassAttention();
                return;
            }

            const pointer = canvas.getPointer(o.e);
            const newPoints = [...polygonPoints, { x: pointer.x, y: pointer.y }];
            setPolygonPoints(newPoints);

            if (newPoints.length > 2) {
                const firstPoint = newPoints[0];
                const distance = Math.sqrt(
                    Math.pow(pointer.x - firstPoint.x, 2) +
                    Math.pow(pointer.y - firstPoint.y, 2)
                );

                // 10 screen px, so closing a polygon works the same when zoomed
                if (distance * (zoomLevel || 1) < 10) {
                    finishPolygon();
                    return;
                }
            }

            if (polygonObj) {
                canvas.remove(polygonObj);
            }

            const fillColor = colorToRgba(selectedClass?.color, 0.25);
            const polygon = new fabric.Polygon(newPoints, {
                fill: fillColor,
                stroke: selectedClass?.color || 'red',
                strokeWidth: BASE_STROKE_WIDTH / zoomLevel,
                selectable: false,
                objectCaching: false,
                // 🔑 This keeps stroke width constant during object scaling
                strokeUniform: true,
                // custom field for class related information
                classData: selectedClass
            });

            canvas.add(polygon);
            setPolygonObj(polygon);
            canvas.requestRenderAll();
        };

        const onKeyDown = (e) => {
            if (e.key === 'Enter' || e.key === 'Escape') {
                finishPolygon();
            }
        };

        if (isDrawingPolygon) {
            canvas.on('mouse:down', onMouseDown);
            window.addEventListener('keydown', onKeyDown);
        }

        return () => {
            // remove by reference: a bare off('mouse:down') would also strip the
            // bounding-box and pan handlers registered by the other hooks
            canvas.off('mouse:down', onMouseDown);
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [canvas, mode, polygonPoints, polygonObj, isDrawingPolygon, currentClassId, zoomLevel, getCurrentImage, updatePolygons, setPolygonPoints, setPolygonObj, setIsDrawingPolygon, classes, BASE_STROKE_WIDTH, saveState]);
};

export default usePolygonDrawing;



